import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { skinsApi } from '../services/skins.api';
import type { SkinId } from '@/features/game/components/board/skins/types';

export const skinsQueryKeys = {
  all: ['skins'] as const,
  owned: () => [...skinsQueryKeys.all, 'owned'] as const,
  trials: () => [...skinsQueryKeys.all, 'trials'] as const
};

export function useUserSkins() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const skinsQuery = useQuery({
    queryKey: skinsQueryKeys.owned(),
    queryFn: skinsApi.getUserSkins,
    enabled: isAuthenticated,
    staleTime: 60_000
  });

  const trialsQuery = useQuery({
    queryKey: skinsQueryKeys.trials(),
    queryFn: skinsApi.getUserSkinTrials,
    enabled: isAuthenticated,
    staleTime: 60_000
  });

  const ownedSkinCodes = useMemo<Set<string>>(() => {
    if (!skinsQuery.data) return new Set(['basics']);
    return new Set(skinsQuery.data.skins.map((s) => s.skinCode));
  }, [skinsQuery.data]);

  const trialedSkinCodes = useMemo<Set<string>>(() => {
    if (!trialsQuery.data) return new Set();
    return new Set(trialsQuery.data.trials.map((t) => t.skinCode));
  }, [trialsQuery.data]);

  const isOwned = (skinId: SkinId): boolean => ownedSkinCodes.has(skinId);

  const isTrialed = (skinId: SkinId): boolean => trialedSkinCodes.has(skinId);

  return {
    ownedSkinCodes,
    trialedSkinCodes,
    isOwned,
    isTrialed,
    isLoading: skinsQuery.isLoading || trialsQuery.isLoading
  };
}
